import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { Play, Pause, Volume2 } from 'lucide-react';

const backgroundImage = `${process.env.PUBLIC_URL || ''}/background/1.png`;
const melodyUrl = `${process.env.PUBLIC_URL || ''}/melody/melody.mp3`;

const HERO_TRANSITION_COLOR = '#FFFFF0';

export function HeroSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionHeight = rect.height;
      // Progreso 0 = arriba del todo, 1 = cuando el hero ha salido lo suficiente
      const scrolled = -rect.top;
      const progress = Math.min(1, Math.max(0, scrolled / sectionHeight));
      setScrollProgress(progress);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const startPlay = () => {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    };
    // Solo intentar autoplay cuando el audio tenga una fuente válida (evita NotSupportedError si falta el archivo)
    audio.addEventListener('canplay', startPlay, { once: true });
    if (audio.readyState >= 3) startPlay();
    return () => audio.removeEventListener('canplay', startPlay);
  }, []);

  const toggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div
        className="section-bg-image transition-opacity duration-150 ease-out"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          opacity: 1 - scrollProgress,
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 z-[1] pointer-events-none transition-opacity duration-150 ease-out"
        style={{
          backgroundColor: HERO_TRANSITION_COLOR,
          opacity: scrollProgress,
        }}
        aria-hidden
      />
      <div className="relative z-10 w-full text-center px-6 py-24 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="flex flex-col items-center gap-0"
        >
          {/* SAVE THE DATE — serif, mayúsculas, letter-spacing amplio
          <p
            className="text-editorial-title text-sm md:text-base tracking-[0.3em] mb-4"
            style={{ fontFamily: 'var(--font-serif)', letterSpacing: '0.3em' }}
          >
            SAVE THE DATE
          </p>

          <p
            className="uppercase text-xs md:text-sm tracking-[0.2em] mb-12"
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 400,
              color: 'var(--emerald)',
              letterSpacing: '0.2em',
            }}
          >
            BODA
          </p>
*/}
          {/* Nombres — script, mucho aire (estilo Bruna e Caio) */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-editorial-names text-5xl md:text-7xl lg:text-8xl mb-4"
            style={{
              fontFamily: 'var(--font-script)',
              color: 'var(--emerald)',
              marginTop: '0.5rem',
              marginBottom: '1.5rem',
            }}
          >
            Samy <span style={{ color: 'var(--chartreuse)', fontFamily: 'inherit' }}>&</span> Dany
          </motion.h1>

          {/* Monograma SD — línea fina, discreto */}
          <p
            className="text-2xl md:text-3xl mb-10 tracking-[0.4em]"
            style={{
              fontFamily: 'var(--font-serif)',
              fontWeight: 300,
              color: 'var(--chartreuse)',
              letterSpacing: '0.35em',
            }}
          >
            SD
          </p>

          {/* Fecha — serif fina, small caps, separadores | */}
          <p
            className="text-editorial-meta text-sm md:text-base mb-2"
            style={{ letterSpacing: '0.2em' }}
          >
            27 | 06 | 2026
          </p>

          {/* Lugar — mismo estilo que fecha */}
          <p
            className="text-editorial-meta text-sm md:text-base mb-14"
            style={{ letterSpacing: '0.2em' }}
          >
            DREAMS JADE, CANCÚN
          </p>

          {/* Botón música — solo iconos Play/Pause y Volume */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={toggleMusic}
            className="flex items-center gap-2 p-2.5 rounded-full border border-[var(--chartreuse)] text-[var(--emerald)] bg-transparent hover:bg-[var(--pale-lime)]/30 transition-colors duration-300"
            aria-label={isPlaying ? 'Pausar música' : 'Reproducir música'}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            <Volume2 className="w-5 h-5" />
          </motion.button>

          <audio
            ref={audioRef}
            loop
            src={melodyUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        </motion.div>
      </div>
    </section>
  );
}
